import React, { useMemo, useRef } from "react";
import { gradientPicker, angularDist, createSeededRandom, toHex } from "../Utils";

const NIGHT_SKY_COLOR = "#101821";
const SUNRISE_COLOR = "#f24b27";
const SUNSET_COLOR = "#bf2b14";
const BLUE_SKY_COLOR = "#7a9fb2";
const SUN_SHINE_COLOR = "#ffd700";
const MOON_SHINE_COLOR = "#fbfadb";

const getHorizonAndSunColors = (
  sunToEastHorizon: number,
  isDay: boolean,
): { [key: string]: string } => {
  let west = "";
  let east = "";

  if (sunToEastHorizon < 18) {
    east = gradientPicker(
      SUNRISE_COLOR,
      isDay ? BLUE_SKY_COLOR : NIGHT_SKY_COLOR,
      sunToEastHorizon,
      [0, 18],
    );
    west = gradientPicker(
      BLUE_SKY_COLOR,
      isDay ? BLUE_SKY_COLOR : NIGHT_SKY_COLOR,
      sunToEastHorizon,
      [0, 18],
    );
  } else if (sunToEastHorizon > 18 && sunToEastHorizon < 180 - 18) {
    east = west = isDay ? BLUE_SKY_COLOR : NIGHT_SKY_COLOR;
  } else {
    west = gradientPicker(
      SUNSET_COLOR,
      isDay ? BLUE_SKY_COLOR : NIGHT_SKY_COLOR,
      180 - sunToEastHorizon,
      [0, 18],
    );
    east = gradientPicker(
      NIGHT_SKY_COLOR,
      isDay ? BLUE_SKY_COLOR : NIGHT_SKY_COLOR,
      180 - sunToEastHorizon,
      [0, 18],
    );
  }

  return { west, east };
};

const generateStars = (outerR: number, count: number, canvas: number[]) => {
  const random = createSeededRandom(5);
  const innerR = outerR * 0.15;

  // Astrolabe centre in element coordinates
  const cx = canvas[0] / 2;
  const cy = canvas[1] / 2;

  // The radial-gradient dot is at the centre of the background-size image
  // (canvas[0] × canvas[1]/2), i.e. at (canvas[0]/2, canvas[1]/4).
  // background-position offsets that image's top-left, so a dot at
  // element coord (tx, ty) needs bg-pos (tx - canvas[0]/2, ty - canvas[1]/4).
  const gradOx = canvas[0] / 2;
  const gradOy = canvas[1] / 4;

  const stars = [];
  for (let i = 0; i < count; i++) {
    const r = innerR + (outerR - innerR) * random();
    const theta = random() * Math.PI; // 0 … π  →  upper semicircle

    // Element coords: centred at astrolabe centre, flipped y for sky half
    const tx = cx + r * Math.cos(theta);
    const ty = cy - r * Math.sin(theta);

    // Convert to background-position values
    const x = tx - gradOx;
    const y = ty - gradOy;

    const duration = 1 + random() * 2;
    const delay = random() * 5;
    const size = 0.5 + random();

    stars.push({
      position: `radial-gradient(circle, white ${size}px, transparent ${size}px) ${x}px ${y}px`,
      animation: `${duration}s infinite alternate ease-in-out ${delay}s`,
    });
  }
  return stars;
};

// ─────────────────────────────────────────────────────────────
// Star field — memoized; only re-renders when visibility or the
// twinkle flag changes, NOT on every animation frame when the
// sun/moon angles update. This is what keeps a rotating sky smooth.
// ─────────────────────────────────────────────────────────────
interface StarFieldProps {
  rDays: number;
  canvas: number[];
  /** false → stars stay visible but static (no twinkling) during animations. */
  twinkle: boolean;
}

const StarField: React.FC<StarFieldProps> = React.memo(
  ({ rDays, canvas, twinkle }) => {
    const stars = useMemo(
      () => generateStars(rDays, 60, canvas),
      [rDays, canvas],
    );
    // Stable per-star twinkle decisions — computed once per star set so the
    // twinkling stars never change identity between renders (re-rolling them
    // every frame was a major source of flicker).
    const twinkleFlags = useMemo(() => {
      const random = createSeededRandom(5);
      return stars.map(() => random() < 0.75);
    }, [stars]);

    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: `${canvas[0]}px`,
          height: `${canvas[1] / 2}px`,
          pointerEvents: "none",
          borderRadius: "50%",
        }}
      >
        {stars.map((star, index) => {
          const animated = twinkle && twinkleFlags[index];
          return (
            <div
              key={index}
              style={{
                position: "absolute",
                width: `${canvas[0]}px`,
                height: `${canvas[1] / 2}px`,
                background: star.position,
                backgroundSize: `${canvas[0]}px ${canvas[1] / 2}px`,
                opacity: twinkle ? undefined : STATIC_STAR_OPACITY,
                animation: animated ? `twinkle ${star.animation}` : "",
              }}
            />
          );
        })}
      </div>
    );
  },
);

// ─────────────────────────────────────────────────────────────
// Horizon sky — only recomputes the expensive conic-gradient
// background at ~10 fps while animating, which prevents the
// flashing/flicker during the celestial interpolation.
// ─────────────────────────────────────────────────────────────
interface HorizonSkyProps {
  canvas: number[];
  gradientEnd: number;
  sun: number;
  moon: number;
  base: number;
  west: number;
  end: number;
  sunBrightness: string;
  moonBrightness: string;
  moonWideness: number;
  horizonWest: string;
  horizonEast: string;
  /** true while interpolating — throttle style updates to ~10 fps. */
  isAnimating: boolean;
}

const THROTTLE_MS = 100;

// Opacity the whole star field holds while a rotation animation runs, so
// dropping the twinkle animations doesn't make the twinkling stars jump to
// full brightness (and back) at the start/end of every rotation.
const STATIC_STAR_OPACITY = 0.7;

const HorizonSky: React.FC<HorizonSkyProps> = React.memo((props) => {
  const lastRenderRef = useRef(0);
  const cachedStyleRef = useRef<React.CSSProperties | null>(null);
  const lastColorsRef = useRef<string | null>(null);

  const now = performance.now();
  const colorKey = `${props.horizonWest}|${props.horizonEast}`;
  const colorChanged = lastColorsRef.current !== colorKey;
  // Throttle intermediate animation frames to ~10 fps, but always recompute
  // when the day/night sky colour changes or once the animation settles — the
  // final frame must never be left stale by the throttle window (e.g. the Sun
  // crossing back above the horizon while moving backward in time).
  const shouldUpdate =
    !props.isAnimating ||
    colorChanged ||
    now - lastRenderRef.current >= THROTTLE_MS;

  if (shouldUpdate || cachedStyleRef.current === null) {
    lastRenderRef.current = now;
    lastColorsRef.current = colorKey;
    cachedStyleRef.current = {
      width: `${props.canvas[0]}px`,
      height: `${props.canvas[1]}px`,
      borderRadius: "50%",
      opacity: 1,
      willChange: "transform",
      transform: "translateZ(0)",
      maskImage: `radial-gradient(circle, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) ${props.gradientEnd}%)`,
      WebkitMaskImage: `radial-gradient(circle, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) ${props.gradientEnd}%)`,
      clipPath: `polygon(0 0%, 100% 0%, 100% 50%, 0% 50%)`,
      background: `
          conic-gradient(
            from ${props.sun - 180}deg,
            #00000000 35deg,
            ${SUN_SHINE_COLOR}${props.sunBrightness} 90deg,
            #00000000 145deg
          ),
          conic-gradient(
            from ${props.moon - 180}deg,
            #00000000 ${90 - props.moonWideness}deg,
            ${MOON_SHINE_COLOR}${props.moonBrightness} 90deg,
            #00000000 ${90 + props.moonWideness}deg
          ),
          conic-gradient(
            from ${props.base - props.west - 90}deg,
            ${props.horizonWest} 0deg,
            ${props.horizonEast} ${props.end}deg,
            #00000000 ${props.end}deg
          )
          `,
    };
  }

  return <div data-testid="horizon-sky" style={cachedStyleRef.current!} />;
});

interface Props {
  rCelestials: number;
  rDays: number;
  rEnd: number;
  east: number;
  west: number;
  sun: number;
  moon: number;
  canvas: number[];
  /** true while interpolating: stars stay visible but stop twinkling. */
  disableTwinkle?: boolean;
}

const Horizon: React.FC<Props> = ({
  rCelestials,
  rDays,
  rEnd,
  east,
  west,
  sun,
  moon,
  canvas,
  disableTwinkle = false,
}) => {
  const sunToWestHorizon = angularDist(sun, west);
  const sunToEastHorizon = angularDist(sun, east);
  const moonToWestHorizon = angularDist(moon, west);
  const sunToMoon = angularDist(sun, moon);

  // Correct coordinates (Astrolabe coordinate starts from 03:00, CSS coordinate starts from 00:00)
  west += 90;
  sun += 90;
  moon += 90;
  east += 90;

  if (west > east) east += 360;

  // Change to the west coordinate
  const base = west;
  const end = east - west;
  sun -= west;
  if (sun < 0) sun += 360;
  moon -= west;
  if (moon < 0) moon += 360;

  const isDay = sun < end;
  const isNight = !isDay;
  const isMoonVisible = moon < end;

  const { west: horizonWest, east: horizonEast } = getHorizonAndSunColors(
    sunToEastHorizon,
    isDay,
  );

  const sunToHorizon = Math.min(sunToWestHorizon, 180 - sunToWestHorizon);
  const moonToHorizon = Math.min(moonToWestHorizon, 180 - moonToWestHorizon);

  const starsOpacity = sunToHorizon < 18 ? sunToHorizon / 18 : 1;

  const moonMaxBrightness = (sunToMoon / 180) * 50;
  const moonBrightness = isMoonVisible
    ? toHex(moonMaxBrightness)
    : !isMoonVisible && moonToHorizon < 18
      ? toHex(moonMaxBrightness * (1 - moonToHorizon / 18))
      : toHex(0);

  const sunBrightness = toHex(
    isNight && sunToHorizon < 18
      ? ((18 - sunToHorizon) / 18) * 50
      : isNight
        ? 0
        : 50,
  );
  const moonWideness = (sunToMoon / 180) * 60;

  const gradientEnd = ((1.45 * rEnd) / canvas[0]) * 100;

  return (
    <foreignObject width={canvas[0]} height={canvas[1]}>
      {/* Horizon sky background — throttled to ~10 fps while animating */}
      <HorizonSky
        canvas={canvas}
        gradientEnd={gradientEnd}
        sun={sun}
        moon={moon}
        base={base}
        west={west}
        end={end}
        sunBrightness={sunBrightness}
        moonBrightness={moonBrightness}
        moonWideness={moonWideness}
        horizonWest={horizonWest}
        horizonEast={horizonEast}
        isAnimating={disableTwinkle}
      />

      {/* Stars are always visible at night — during a rotation animation they
          simply stop twinkling (a static star field is far cheaper than 60 CSS
          twinkle animations running against a rotating group), instead of
          fading out entirely. */}
      <div
        style={{
          opacity: isNight ? starsOpacity : 0,
          transition: "opacity 1s ease-in-out",
        }}
      >
        {isNight && (
          <StarField rDays={rDays} canvas={canvas} twinkle={!disableTwinkle} />
        )}
      </div>

      <style>
        {`@keyframes twinkle { 0% { opacity: 0.3; } 50% { opacity: 1; } 100% { opacity: 0.3; } }`}
      </style>
    </foreignObject>
  );
};

export default React.memo(Horizon);
